import { useMemo } from 'react'
import ReactECharts from 'echarts-for-react'
import { useTranslation } from 'react-i18next'
import { useTheme } from '../../contexts/ThemeContext'
import { cssToken, formatKPIValue, type Decomposition } from './benchmark-utils'

/**
 * From the cohort median to this building, trade by trade (spec 2026-09-13-01).
 *
 * The steps never sum exactly to the gap — the sum of per-trade medians is not
 * the median of totals — so the last step is named for what it is,
 * "non-additivity of medians", rather than being spread silently over the
 * others or dressed up as a composition effect.
 */
interface Props {
    decomposition: Decomposition
    tradeLabel: (key: string) => string
    locale: string
}

export function GapWaterfall({ decomposition, tradeLabel, locale }: Props) {
    const { t } = useTranslation('benchmark')
    useTheme()
    const { currency } = decomposition

    const option = useMemo(() => {
        const median = decomposition.median ?? 0
        const value = decomposition.value ?? 0
        const muted = cssToken('--muted-foreground', '#6b7280')
        const up = cssToken('--chart-4', '#dc2626')
        const down = cssToken('--chart-2', '#16a34a')
        const neutral = cssToken('--border', '#d1d5db')
        const foreground = cssToken('--foreground', '#0a0a0a')
        const fmt = (v: number) => formatKPIValue('currency', v, currency, locale)
        const signed = (v: number) => `${v > 0 ? '+' : v < 0 ? '−' : ''}${fmt(Math.abs(v))}`

        const categories: string[] = [t('waterfall.median')]
        const base: number[] = [0]
        const bars: { value: number; itemStyle: { color: string }; label: string }[] = [
            { value: median, itemStyle: { color: muted }, label: fmt(median) },
        ]
        let running = median
        for (const step of decomposition.steps) {
            const name =
                step.kind === 'median_non_additivity'
                    ? t('waterfall.median_non_additivity')
                    : tradeLabel(step.key)
            categories.push(name)
            const next = running + step.delta
            base.push(Math.min(running, next))
            bars.push({
                value: Math.abs(step.delta),
                itemStyle: { color: step.kind === 'median_non_additivity' ? neutral : step.delta > 0 ? up : down },
                label: signed(step.delta),
            })
            running = next
        }
        categories.push(t('waterfall.value'))
        base.push(0)
        bars.push({ value, itemStyle: { color: cssToken('--primary', '#4f46e5') }, label: fmt(value) })

        return {
            animation: false,
            grid: { left: 64, right: 16, top: 24, bottom: 72 },
            tooltip: {
                trigger: 'axis',
                axisPointer: { type: 'shadow' },
                formatter: (params: { dataIndex: number }[]) => {
                    const i = params[1]?.dataIndex ?? params[0]?.dataIndex ?? 0
                    return `${categories[i]} : ${bars[i].label}`
                },
            },
            xAxis: {
                type: 'category',
                data: categories,
                axisLabel: { color: muted, interval: 0, rotate: categories.length > 6 ? 30 : 0, fontSize: 11 },
            },
            yAxis: {
                type: 'value',
                axisLabel: { color: muted, formatter: fmt },
                splitLine: { lineStyle: { color: neutral, opacity: 0.5 } },
            },
            series: [
                { type: 'bar', stack: 'gap', silent: true, itemStyle: { color: 'transparent' }, data: base },
                {
                    type: 'bar',
                    stack: 'gap',
                    barMaxWidth: 40,
                    data: bars.map((b) => ({ value: b.value, itemStyle: { ...b.itemStyle, borderRadius: 3 } })),
                    label: {
                        show: true,
                        position: 'top',
                        color: foreground,
                        fontSize: 11,
                        formatter: (p: { dataIndex: number }) => bars[p.dataIndex].label,
                    },
                },
            ],
        }
    }, [decomposition, currency, locale, t, tradeLabel])

    return <ReactECharts option={option} style={{ height: 320, width: '100%' }} notMerge opts={{ renderer: 'canvas' }} />
}
